// @ts-nocheck
import { useState, useEffect, useRef } from 'react'
import { useCallStore } from '../stores/callStore'
import { useApi } from '../hooks/useApi'
import { useAuthStore } from '../stores/authStore'
import { useCallHistory } from '../hooks/useCallHistory'
import InCallNoteEditor from './InCallNoteEditor'
import CallHistoryTable from './CallHistoryTable'
import CallMemoEditor from './CallMemoEditor'
import { ChevronRight, ChevronLeft, User, Phone, Mail, Building, Tag, X, Save, GripVertical, Star, Lightbulb, ScrollText, FileEdit, Edit2, Ban, ShieldAlert } from 'lucide-react'
import { formatPhoneNumberForDisplay, isAnonymousNumber, formatPhoneNumberAsYouType, formatPhoneNumberForSave, isValidPhoneNumber } from '../utils/phoneNumberHelper'
import { checkBlockStatus, blockFromCallMemo, unblockNumber } from '../utils/blocklistHelper'

/**
 * 通話中に顧客情報を表示するサイドパネル
 */
export default function CustomerSidePanel() {
  const { activeCall, currentCallId, isInCall, callStatus, callerNumber, internalCallStatus, currentCallHistoryId } = useCallStore()
  const { user } = useAuthStore()
  const [usedPhoneNumber, setUsedPhoneNumber] = useState(null) // 使用した自社の電話番号
  const api = useApi()
  const [customer, setCustomer] = useState(null)
  const [note, setNote] = useState('')
  const [tags, setTags] = useState([])
  const [isExpanded, setIsExpanded] = useState(true)
  const [loading, setLoading] = useState(false)
  const [showPanel, setShowPanel] = useState(false)
  const [isCallEnded, setIsCallEnded] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [newCustomerName, setNewCustomerName] = useState('')
  const [newCustomerCompany, setNewCustomerCompany] = useState('')
  const [manualPhoneNumber, setManualPhoneNumber] = useState('') // 手動入力の電話番号
  const [isEditingPhoneNumber, setIsEditingPhoneNumber] = useState(false) // 電話番号編集モード
  const [phoneNumberError, setPhoneNumberError] = useState('') // 電話番号エラー
  const [panelWidth, setPanelWidth] = useState(500) // デフォルト500px
  const [isResizing, setIsResizing] = useState(false)
  const [selectedCall, setSelectedCall] = useState(null)
  const [showMemoEditor, setShowMemoEditor] = useState(false)
  const callStartTimeRef = useRef(null)
  const callSidRef = useRef(null)
  const currentCallIdRef = useRef(null) // currentCallIdを保持（通話終了後も使えるように）
  const callHistoryIdRef = useRef(null) // callHistoryIdを保持（通話終了後も使えるように）
  const noteSegmentIdRef = useRef(null) // 通話中に作成したメモセグメントIDを保持（更新用）
  const lastCallerNumberRef = useRef(null)
  const panelRef = useRef(null)
  const lastSavedNoteRef = useRef('') // 最後に保存したメモ内容
  const [saveStatus, setSaveStatus] = useState('saved') // 'saved' | 'saving' | 'unsaved'
  const [lastSaved, setLastSaved] = useState(null)

  // ブロック機能のステート
  const [blockStatus, setBlockStatus] = useState(null) // { isBlocked, blockInfo }
  const [isBlockLoading, setIsBlockLoading] = useState(false)
  const [showBlockConfirm, setShowBlockConfirm] = useState(false)
  const [blockReason, setBlockReason] = useState('迷惑電話')

  // 通話履歴フック
  const {
    callHistory,
    loading: callHistoryLoading,
    fetchCallHistory,
    refresh: refreshCallHistory,
    createNoteSegment
  } = useCallHistory({
    autoFetch: false,
    enableRealtimeUpdates: false,  // WebSocket購読を無効化（CallHistoryPageで購読済み）
    isolatedState: true  // グローバルストアを汚さないようローカル状態を使用
  })

  // 通話状態の監視とパネル表示制御
  useEffect(() => {
    // 内線通話の場合はパネルを表示しない（既に開いている場合を除く）
    const isInternalCall = internalCallStatus === 'connected' || internalCallStatus === 'ringing'

    if (isInCall() && !isInternalCall) {
      // 外線通話中の場合、パネルを表示
      setShowPanel(true)
      setIsCallEnded(false)

      // 新しい通話が開始された場合、古いrefsをリセットして新しい値で初期化
      // currentCallIdが変わった＝別の通話 → refsを更新
      if (currentCallId && currentCallIdRef.current && currentCallId !== currentCallIdRef.current) {
        console.log('[CustomerSidePanel] 新しい通話を検出 - refsをリセット:', {
          old: currentCallIdRef.current,
          new: currentCallId,
        })
        callSidRef.current = null
        currentCallIdRef.current = null
        callHistoryIdRef.current = null
        noteSegmentIdRef.current = null
        callStartTimeRef.current = null
        lastSavedNoteRef.current = ''
        setNote('')
        setTags([])
      }

      // 通話開始時にCallSidを記録（activeCallがあれば即座に）
      if (activeCall) {
        const callSid = activeCall.parameters?.CallSid || activeCall.parameters?.callSid
        if (callSid && !callSidRef.current) {
          callSidRef.current = callSid
          console.log('CallSid記録:', callSid)
        }
      }

      // currentCallId（親CallSid）を記録（通話終了後の検索用）
      if (currentCallId && !currentCallIdRef.current) {
        currentCallIdRef.current = currentCallId
        console.log('currentCallId記録:', currentCallId)
      }

      // callHistoryIdを記録（通話終了後も使えるように）
      // 新しいcallHistoryIdが来た場合は更新（新しい通話のIDを確実に使用）
      if (currentCallHistoryId && currentCallHistoryId !== callHistoryIdRef.current) {
        callHistoryIdRef.current = currentCallHistoryId
        console.log('callHistoryId記録:', currentCallHistoryId)

        // 通話履歴から電話番号を取得
        fetchUsedPhoneNumber(currentCallHistoryId)
      }

      // 開始時刻を記録（初回のみ）
      if (!callStartTimeRef.current) {
        callStartTimeRef.current = new Date().toISOString()
        console.log('通話開始時刻記録:', callStartTimeRef.current)
      }

      // 発信者番号を記録
      if (callerNumber) {
        lastCallerNumberRef.current = callerNumber
      }
    } else if (showPanel && !isCallEnded && !isInternalCall) {
      // 通話が終了した場合、終了フラグを立てる（パネルは閉じない）
      // 内線通話終了時はパネルを表示しないので何もしない
      setIsCallEnded(true)
      console.log('通話終了検出 - 保存データ:', {
        callSid: callSidRef.current,
        callHistoryId: callHistoryIdRef.current,
        startTime: callStartTimeRef.current,
        note: note ? '有り' : '無し',
        tags: tags.length > 0 ? tags : '無し'
      })

      // 安全策: 通話終了時にタグをCallHistoryに最終保存
      // auto-save effectはisInCall()がfalseになると実行されないため、
      // 通話終了直前にタグが変更された場合のレースコンディションを防止
      if (callHistoryIdRef.current && tags.length > 0) {
        console.log('[通話終了タグ保存] 実行:', { callHistoryId: callHistoryIdRef.current, tags })
        api.put(`/api/call-history/${callHistoryIdRef.current}`, { tags })
          .then(() => console.log('[通話終了タグ保存] 成功'))
          .catch(err => console.error('[通話終了タグ保存] 失敗:', err))
      }
    }
  }, [activeCall, callStatus, isInCall, callerNumber, showPanel, isCallEnded, note, tags, internalCallStatus, currentCallHistoryId, currentCallId, api])

  // 通話履歴から使用した電話番号を取得
  const fetchUsedPhoneNumber = async (callHistoryId) => {
    try {
      console.log('[CustomerSidePanel] 通話履歴から電話番号を取得:', callHistoryId)
      const callData = await api.get(`/api/call-history/${callHistoryId}`)
      console.log('[CustomerSidePanel] 取得した通話履歴:', callData)
      
      // 利用した自社の電話番号（着信時はto、発信時はfrom）
      const phoneNumber = callData.originalDirection === 'inbound' ? callData.to : callData.from
      console.log('[CustomerSidePanel] 使用した電話番号:', phoneNumber)
      setUsedPhoneNumber(phoneNumber)
    } catch (err) {
      console.error('[CustomerSidePanel] 電話番号取得エラー:', err)
      setUsedPhoneNumber(null)
    }
  }

  // 顧客情報と通話履歴を取得
  useEffect(() => {
    const fetchCustomerData = async () => {
      const phoneNumber = callerNumber || lastCallerNumberRef.current
      if (!phoneNumber) return

      // 非通知の場合は顧客検索をスキップ
      if (isAnonymousNumber(phoneNumber)) {
        setCustomer(null)
        setLoading(false)
        return
      }

      setLoading(true)
      try {
        // 顧客情報を電話番号で検索
        const customersData = await api.get(`/api/customers?search=${encodeURIComponent(phoneNumber)}&limit=1`)

        if (customersData?.customers?.length > 0) {
          const foundCustomer = customersData.customers[0]
          setCustomer(foundCustomer)

          // 通話履歴を取得（最近5件）- useCallHistoryフックを使用
          fetchCallHistory({ customerId: foundCustomer.customerId, limit: 5 })
        } else {
          // 新規顧客
          setCustomer(null)
        }
      } catch (err) {
        console.error('顧客情報取得エラー:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchCustomerData()
  }, [callerNumber, api, fetchCallHistory])

  // ブロック状態をチェック
  useEffect(() => {
    const checkBlock = async () => {
      const phoneNumber = callerNumber || lastCallerNumberRef.current
      if (!phoneNumber || isAnonymousNumber(phoneNumber)) {
        setBlockStatus(null)
        return
      }

      try {
        const status = await checkBlockStatus(phoneNumber)
        setBlockStatus(status)
      } catch (err) {
        console.error('ブロック状態チェックエラー:', err)
        setBlockStatus(null)
      }
    }

    checkBlock()
  }, [callerNumber])

  // ブロック処理
  const handleBlock = async () => {
    const phoneNumber = callerNumber || lastCallerNumberRef.current
    if (!phoneNumber || isAnonymousNumber(phoneNumber)) {
      alert('電話番号が不明なためブロックできません')
      return
    }

    setIsBlockLoading(true)
    try {
      await blockFromCallMemo(phoneNumber, note, blockReason)
      
      // ブロック状態を更新
      const status = await checkBlockStatus(phoneNumber)
      setBlockStatus(status)
      setShowBlockConfirm(false)
      setBlockReason('迷惑電話')
      
      alert('この番号をブロックリストに追加しました')
    } catch (err) {
      console.error('ブロックエラー:', err)
      alert('ブロックに失敗しました: ' + err.message)
    } finally {
      setIsBlockLoading(false)
    }
  }

  // ブロック解除処理
  const handleUnblock = async () => {
    const phoneNumber = callerNumber || lastCallerNumberRef.current
    if (!phoneNumber || !blockStatus?.blockInfo?.blockId) {
      return
    }

    if (!confirm('この番号のブロックを解除しますか？')) {
      return
    }

    setIsBlockLoading(true)
    try {
      await unblockNumber(blockStatus.blockInfo.blockId)
      
      // ブロック状態を更新
      const status = await checkBlockStatus(phoneNumber)
      setBlockStatus(status)
      
      alert('ブロックを解除しました')
    } catch (err) {
      console.error('ブロック解除エラー:', err)
      alert('ブロック解除に失敗しました: ' + err.message)
    } finally {
      setIsBlockLoading(false)
    }
  }

  // リサイズ機能
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return
      
      // 画面右端からの距離を計算
      const newWidth = window.innerWidth - e.clientX
      
      // 最小幅300px、最大幅800px
      const constrainedWidth = Math.min(Math.max(newWidth, 300), 800)
      setPanelWidth(constrainedWidth)
    }

    const handleMouseUp = () => {
      setIsResizing(false)
      document.body.style.cursor = 'default'
      document.body.style.userSelect = 'auto'
    }

    if (isResizing) {
      document.body.style.cursor = 'ew-resize'
      document.body.style.userSelect = 'none'
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isResizing])

  const handleResizeStart = (e) => {
    e.preventDefault()
    setIsResizing(true)
  }

  // 電話番号の手動入力処理
  const handlePhoneNumberChange = (e) => {
    const value = e.target.value
    const formatted = formatPhoneNumberAsYouType(value)
    setManualPhoneNumber(formatted)
    setPhoneNumberError('')
  }

  // 電話番号の確定処理
  const handlePhoneNumberConfirm = async () => {
    if (!manualPhoneNumber) {
      setPhoneNumberError('電話番号を入力してください')
      return
    }

    // 電話番号のバリデーション
    const phoneNumberInternational = formatPhoneNumberForSave(manualPhoneNumber)
    if (!phoneNumberInternational) {
      setPhoneNumberError('有効な電話番号を入力してください')
      return
    }

    // 入力された電話番号で顧客を検索
    setLoading(true)
    try {
      const customersData = await api.get(`/api/customers?search=${encodeURIComponent(phoneNumberInternational)}&limit=1`)

      if (customersData?.customers?.length > 0) {
        // 既存顧客が見つかった
        const foundCustomer = customersData.customers[0]
        setCustomer(foundCustomer)
        fetchCallHistory({ customerId: foundCustomer.customerId, limit: 5 })
        setIsEditingPhoneNumber(false)
        setManualPhoneNumber('')
      } else {
        // 新規顧客として扱う - 電話番号を更新
        lastCallerNumberRef.current = phoneNumberInternational
        setCustomer(null)
        setIsEditingPhoneNumber(false)
      }
    } catch (err) {
      console.error('顧客検索エラー:', err)
      setPhoneNumberError('顧客検索中にエラーが発生しました')
    } finally {
      setLoading(false)
    }
  }

  // 電話番号編集のキャンセル
  const handlePhoneNumberCancel = () => {
    setIsEditingPhoneNumber(false)
    setManualPhoneNumber('')
    setPhoneNumberError('')
  }

  // 新規顧客を作成（まだ未登録の場合のみ）
  const ensureCustomerCreated = async (): Promise<void> => {
    if (customer) return // 既存顧客がある場合は何もしない

    const phoneNumber = lastCallerNumberRef.current || callerNumber
    if (!phoneNumber || isAnonymousNumber(phoneNumber)) return

    const phoneNumberInternational = formatPhoneNumberForSave(phoneNumber)
    if (!phoneNumberInternational) return

    try {
      console.log('[CustomerSidePanel] 新規顧客を作成:', { phoneNumber: phoneNumberInternational, name: newCustomerName })
      const created = await api.post('/api/customers', {
        phoneNumber: phoneNumberInternational,
        name: newCustomerName.trim(),
        companyName: newCustomerCompany.trim(),
      })
      setCustomer(created)
      console.log('[CustomerSidePanel] 顧客作成完了:', created.customerId)
    } catch (err) {
      // 409 = 既に登録済み → 検索して取得
      if (err.response?.status === 409 && err.response?.data?.customer) {
        setCustomer(err.response.data.customer)
        console.log('[CustomerSidePanel] 既存顧客を使用:', err.response.data.customer.customerId)
      } else {
        console.error('[CustomerSidePanel] 顧客作成エラー:', err)
      }
    }
  }

  // メモを手動保存（通話中に即座に保存）
  const handleManualSave = async () => {
    if (!note || !note.trim()) {
      console.warn('メモが空のため保存をスキップ')
      return
    }

    if (!callHistoryIdRef.current) {
      console.error('callHistoryIdが利用できません')
      alert('通話履歴IDが見つかりません。メモを保存できません。')
      return
    }

    setIsSaving(true)
    try {
      // 新規顧客の場合、メモ保存前に顧客を作成
      await ensureCustomerCreated()

      const startTime = callStartTimeRef.current
      const callHistoryId = callHistoryIdRef.current

      // 既存のメモセグメントがあれば更新、なければ新規作成
      if (noteSegmentIdRef.current) {
        console.log('手動保存: メモセグメントを更新:', noteSegmentIdRef.current)
        await api.put(`/api/note-segments/${noteSegmentIdRef.current}`, {
          note: note,
          callType: '',
          summary: '',
          tags: tags || [],
        })
        console.log('メモセグメントを手動更新しました')
      } else {
        console.log('手動保存: 新規メモセグメントを作成:', callHistoryId)
        const response = await api.post(`/api/call-history/${callHistoryId}/note-segments`, {
          note: note,
          callType: '',
          summary: '',
          tags: tags || [],
          clientCreatedAt: startTime, // 通話開始時刻を使用
        })
        // 作成したメモセグメントIDを保存（次回は更新）
        noteSegmentIdRef.current = response.noteSegmentId
        console.log('メモセグメントを新規作成しました:', noteSegmentIdRef.current)
      }

      console.log('手動保存が完了しました')
    } catch (error) {
      console.error('手動保存エラー:', error)
      alert('メモの保存に失敗しました: ' + error.message)
    } finally {
      setIsSaving(false)
    }
  }

  // メモを保存してCallHistoryにNoteSegmentを追加（通話終了時用）
  const saveCallHistory = async () => {
    if (!note || !callStartTimeRef.current) {
      console.warn('保存条件が満たされていません:', { note: !!note, startTime: !!callStartTimeRef.current })
      return false
    }

    if (!callHistoryIdRef.current) {
      console.error('callHistoryIdが利用できません')
      alert('通話履歴IDが見つかりません。メモを保存できません。')
      return false
    }

    // 新規顧客の場合、通話終了時にも顧客を作成
    await ensureCustomerCreated()

    // 既にメモセグメントが作成されている場合は、最終更新のみ行う
    if (noteSegmentIdRef.current) {
      console.log('通話終了: 既存のメモセグメントを最終更新:', noteSegmentIdRef.current)
      setIsSaving(true)
      try {
        await api.put(`/api/note-segments/${noteSegmentIdRef.current}`, {
          note: note,
          callType: '',
          summary: '',
          tags: tags || [],
        })
        console.log('メモセグメントを最終更新しました')
        
        // ローカルストレージから削除
        if (callSidRef.current) {
          localStorage.removeItem(`call-note-${callSidRef.current}`)
        }
        
        return true
      } catch (error) {
        console.error('通話履歴保存エラー:', error)
        alert('メモの保存に失敗しました: ' + error.message)
        return false
      } finally {
        setIsSaving(false)
      }
    }

    // メモセグメントが未作成の場合のみ新規作成
    setIsSaving(true)
    try {
      const startTime = callStartTimeRef.current
      const callHistoryId = callHistoryIdRef.current

      console.log('通話終了: 新規メモセグメントを作成:', callHistoryId)

      // CallNoteSegmentを作成（callHistoryIdを直接使用）
      const response = await api.post(`/api/call-history/${callHistoryId}/note-segments`, {
        note: note,
        callType: '',
        summary: '',
        tags: tags || [],
        clientCreatedAt: startTime, // 通話開始時刻を使用
      })
      noteSegmentIdRef.current = response.noteSegmentId
      console.log('メモセグメントを新規作成しました:', noteSegmentIdRef.current)

      // ローカルストレージから削除
      if (callSidRef.current) {
        localStorage.removeItem(`call-note-${callSidRef.current}`)
      }
      
      return true
    } catch (error) {
      console.error('通話履歴保存エラー:', error)
      alert('メモの保存に失敗しました: ' + error.message)
      return false
    } finally {
      setIsSaving(false)
    }
  }

  // シンプルな自動保存（デバウンス方式：入力停止後30秒で保存）
  useEffect(() => {
    // 通話中でない、または callHistoryId がない場合はスキップ
    if (!isInCall() || isCallEnded || !callHistoryIdRef.current) {
      return
    }

    // メモが空の場合はスキップ
    if (!note || !note.trim()) {
      setSaveStatus('saved')
      return
    }

    // 前回保存した内容と同じ場合はスキップ
    if (note === lastSavedNoteRef.current) {
      return
    }

    // 未保存状態にする
    setSaveStatus('unsaved')

    // 30秒後に保存するタイマーをセット
    const timer = setTimeout(async () => {
      console.log('[自動保存] 実行開始')
      setSaveStatus('saving')
      
      try {
        if (noteSegmentIdRef.current) {
          // 既存のメモセグメントを更新
          await api.put(`/api/note-segments/${noteSegmentIdRef.current}`, {
            note,
            callType: '',
            summary: '',
            tags: tags || [],
          })
        } else {
          // 新規メモセグメントを作成
          const response = await api.post(`/api/call-history/${callHistoryIdRef.current}/note-segments`, {
            note,
            callType: '',
            summary: '',
            tags: tags || [],
            clientCreatedAt: callStartTimeRef.current,
          })
          noteSegmentIdRef.current = response.noteSegmentId
        }
        
        lastSavedNoteRef.current = note
        setSaveStatus('saved')
        setLastSaved(new Date())
        console.log('[自動保存] 成功')
      } catch (error) {
        console.error('[自動保存] 失敗:', error)
        setSaveStatus('unsaved')
      }
    }, 30000) // 30秒

    // クリーンアップ：次の入力があったらタイマーをキャンセル
    return () => clearTimeout(timer)
  }, [note, tags, isInCall, isCallEnded, api])

  // タグが変更されたときに即座にCallHistoryに保存
  useEffect(() => {
    // 通話中でない、または callHistoryId がない場合はスキップ
    if (!isInCall() || isCallEnded || !callHistoryIdRef.current) {
      console.log('[タグ自動保存] スキップ:', {
        isInCall: isInCall(),
        isCallEnded,
        callHistoryId: callHistoryIdRef.current,
        tags,
      })
      return
    }

    // 初回レンダリング時はスキップ（tagsの初期値設定時）
    if (tags.length === 0 && !noteSegmentIdRef.current) {
      return
    }

    // タグをCallHistoryに直接保存
    const saveTagsImmediately = async () => {
      console.log('[タグ自動保存] 実行開始 - CallHistoryに直接保存:', {
        callHistoryId: callHistoryIdRef.current,
        tags,
      })
      setSaveStatus('saving')

      try {
        // CallHistoryに直接タグを保存
        await api.put(`/api/call-history/${callHistoryIdRef.current}`, {
          tags: tags || [],
        })
        
        setSaveStatus('saved')
        setLastSaved(new Date())
        console.log('[タグ自動保存] 成功 - CallHistory更新完了')
      } catch (error) {
        console.error('[タグ自動保存] 失敗:', error)
        setSaveStatus('error')
      }
    }

    saveTagsImmediately()
  }, [tags, isInCall, isCallEnded, api])

  // 保存して閉じる
  const handleSaveAndClose = async () => {
    // 顧客登録はメモの有無に関係なく実行
    await ensureCustomerCreated()

    if (note) {
      const success = await saveCallHistory()
      if (!success) return
    }

    // タグをCallHistoryに最終保存（auto-saveが漏れた場合の安全策）
    if (callHistoryIdRef.current && tags.length > 0) {
      try {
        console.log('[handleSaveAndClose] タグを最終保存:', { callHistoryId: callHistoryIdRef.current, tags })
        await api.put(`/api/call-history/${callHistoryIdRef.current}`, { tags })
      } catch (err) {
        console.error('[handleSaveAndClose] タグ最終保存エラー:', err)
      }
    }

    // ローカルストレージから削除（バックアップ用のデータをクリーンアップ）
    if (callSidRef.current) {
      localStorage.removeItem(`call-note-${callSidRef.current}`)
    }

    // パネルを閉じてリセット
    setShowPanel(false)
    setNote('')
    setTags([])
    setCustomer(null)
    setIsCallEnded(false)
    setNewCustomerName('')
    setNewCustomerCompany('')
    setManualPhoneNumber('')
    setIsEditingPhoneNumber(false)
    setPhoneNumberError('')
    callStartTimeRef.current = null
    callSidRef.current = null
    currentCallIdRef.current = null
    callHistoryIdRef.current = null
    noteSegmentIdRef.current = null // メモセグメントIDもクリア
    lastCallerNumberRef.current = null
  }

  // 保存せずに閉じる（メモは保存しないが、タグはCallHistoryに保存済みのため最終保存する）
  const handleCloseWithoutSave = () => {
    if (note && !confirm('入力したメモは保存されません。閉じてもよろしいですか？')) {
      return
    }

    // タグはCallHistoryに最終保存（auto-saveが漏れた場合の安全策）
    if (callHistoryIdRef.current && tags.length > 0) {
      api.put(`/api/call-history/${callHistoryIdRef.current}`, { tags })
        .catch(err => console.error('[handleCloseWithoutSave] タグ最終保存エラー:', err))
    }

    // ローカルストレージから削除
    if (callSidRef.current) {
      localStorage.removeItem(`call-note-${callSidRef.current}`)
    }

    // パネルを閉じてリセット
    setShowPanel(false)
    setNote('')
    setTags([])
    setCustomer(null)
    setIsCallEnded(false)
    setNewCustomerName('')
    setNewCustomerCompany('')
    setManualPhoneNumber('')
    setIsEditingPhoneNumber(false)
    setPhoneNumberError('')
    callStartTimeRef.current = null
    callSidRef.current = null
    currentCallIdRef.current = null
    callHistoryIdRef.current = null
    noteSegmentIdRef.current = null // メモセグメントIDもクリア
    lastCallerNumberRef.current = null
  }

  // 通話詳細編集
  const handleEditMemo = (call) => {
    setSelectedCall(call)
    setShowMemoEditor(true)
  }

  // メモ編集保存後
  const handleMemoSave = () => {
    setShowMemoEditor(false)
    setSelectedCall(null)
    if (customer?.customerId) {
      fetchCallHistory({ customerId: customer.customerId, limit: 5 })
    }
  }

  const getPriorityBadge = (priority) => {
    const badges = {
      vip: (
        <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 text-xs rounded-full font-semibold flex items-center gap-1">
          <Star className="w-3 h-3" />
          VIP
        </span>
      ),
      high: <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 text-xs rounded-full font-semibold">優先</span>,
      normal: null,
      low: <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-xs rounded-full">低</span>
    }
    return badges[priority] || null
  }

  // パネルが表示状態でない場合は非表示
  if (!showPanel) {
    return null
  }

  return (
    <aside
      ref={panelRef}
      style={{ width: isExpanded ? `${panelWidth}px` : '48px' }}
      className="fixed right-0 top-0 bottom-0 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-xl z-50"
    >
      {/* リサイズハンドル */}
      {isExpanded && (
        <div
          onMouseDown={handleResizeStart}
          className="absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize hover:bg-blue-400 dark:hover:bg-blue-600 transition-colors group"
          title="ドラッグして幅を調整"
        >
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
            <GripVertical className="w-4 h-4 text-blue-500 dark:text-blue-400" />
          </div>
        </div>
      )}

      {/* 折りたたみボタン */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 w-6 h-12 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-l-lg shadow-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center justify-center z-10"
      >
        {isExpanded ? <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-400" /> : <ChevronLeft className="w-4 h-4 text-gray-600 dark:text-gray-400" />}
      </button>

      {isExpanded && (
        <div className="h-full overflow-y-auto flex flex-col">
          {/* ヘッダー */}
          <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 shadow-md z-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <User className="w-5 h-5" />
                <h3 className="font-semibold text-lg">
                  {isCallEnded ? '通話終了 - メモ保存' : '通話中の顧客情報'}
                </h3>
              </div>
              {isCallEnded && (
                <button
                  onClick={handleCloseWithoutSave}
                  className="p-1 hover:bg-white/20 rounded transition"
                  title="閉じる"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
            {isCallEnded && (
              <p className="text-sm text-blue-100 mt-2">
                メモを入力して保存してください
              </p>
            )}
          </div>

          <div className="p-4 space-y-6">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : customer ? (
              <>
                {/* 顧客情報カード */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                  {/* 名前、優先度、統計を1行に */}
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                        {customer.name || '名前未登録'}
                      </h3>
                      {getPriorityBadge(customer.priority)}
                    </div>
                    {/* 通話統計をコンパクトに表示（顧客テーブルの統計を使用） */}
                    <div className="flex items-center gap-3 text-xs bg-white/50 dark:bg-gray-800/50 px-2 py-1 rounded">
                      <div className="text-center">
                        <div className="text-gray-600 dark:text-gray-400">通話</div>
                        <div className="font-bold text-gray-900 dark:text-white">
                          {customer.stats?.totalCalls || 0}回
                        </div>
                      </div>
                      <div className="w-px h-6 bg-gray-300 dark:bg-gray-600"></div>
                      <div className="text-center">
                        <div className="text-gray-600 dark:text-gray-400">最終</div>
                        <div className="font-medium text-gray-900 dark:text-white whitespace-nowrap">
                          {customer.stats?.lastCallAt
                            ? new Date(customer.stats.lastCallAt).toLocaleDateString('ja-JP', { month: '2-digit', day: '2-digit' })
                            : '-'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 会社情報 */}
                  {customer.companyName && (
                    <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                      <Building className="w-4 h-4" />
                      <span>{customer.companyName}</span>
                    </div>
                  )}
                  
                  {/* 役職 */}
                  {customer.position && (
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                      {customer.department && `${customer.department} `}
                      {customer.position}
                    </p>
                  )}

                  {/* 連絡先 */}
                  <div className="space-y-2 text-sm mb-3">
                    <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                      <Phone className="w-4 h-4" />
                      <span className="font-mono">{formatPhoneNumberForDisplay(customer.phoneNumber)}</span>
                    </div>
                    {customer.email && (
                      <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                        <Mail className="w-4 h-4" />
                        <span className="text-xs">{customer.email}</span>
                      </div>
                    )}
                  </div>

                  {/* タグ */}
                  {customer.tags && customer.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {customer.tags.map(tag => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* 重要メモ */}
                  {customer.notes && (
                    <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded text-xs">
                      <div className="font-medium text-yellow-800 dark:text-yellow-300 mb-1 flex items-center gap-1">
                        <Tag className="w-3 h-3" />
                        重要メモ:
                      </div>
                      <p className="text-gray-700 dark:text-gray-300">{customer.notes}</p>
                    </div>
                  )}
                </div>
              </>
            ) : (
              /* 新規顧客 */
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
                <div className="flex items-center gap-2 mb-3">
                  <User className="w-5 h-5 text-green-600 dark:text-green-400" />
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                    {isCallEnded ? '新規のお客様（通話終了）' : '新規のお客様'}
                  </h3>
                </div>
                
                {/* 電話番号表示・編集 */}
                {isEditingPhoneNumber ? (
                  <div className="mb-4">
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      電話番号を入力
                    </label>
                    <div className="space-y-2">
                      <input
                        type="tel"
                        value={manualPhoneNumber}
                        onChange={handlePhoneNumberChange}
                        placeholder="090-1234-5678"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white"
                        autoFocus
                      />
                      {phoneNumberError && (
                        <p className="text-xs text-red-600 dark:text-red-400">{phoneNumberError}</p>
                      )}
                      <div className="flex gap-2">
                        <button
                          onClick={handlePhoneNumberConfirm}
                          disabled={loading}
                          className="flex-1 px-3 py-1.5 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50"
                        >
                          {loading ? '確認中...' : '確定'}
                        </button>
                        <button
                          onClick={handlePhoneNumberCancel}
                          disabled={loading}
                          className="px-3 py-1.5 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded text-sm hover:bg-gray-300 dark:hover:bg-gray-500"
                        >
                          キャンセル
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-2 text-sm font-mono bg-white dark:bg-gray-700 p-2 rounded mb-4 border border-green-300 dark:border-green-700">
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-green-600 dark:text-green-400" />
                      <span className="text-gray-900 dark:text-white font-semibold">
                        {formatPhoneNumberForDisplay(lastCallerNumberRef.current || callerNumber) || '番号不明'}
                      </span>
                    </div>
                    {isAnonymousNumber(lastCallerNumberRef.current || callerNumber) && (
                      <button
                        onClick={() => setIsEditingPhoneNumber(true)}
                        className="flex items-center gap-1 px-2 py-1 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded hover:bg-green-200 dark:hover:bg-green-900/50"
                        title="電話番号を手動入力"
                      >
                        <Edit2 className="w-3 h-3" />
                        入力
                      </button>
                    )}
                  </div>
                )}

                {/* 新規顧客情報入力フォーム */}
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      お名前
                    </label>
                    <input
                      type="text"
                      value={newCustomerName}
                      onChange={(e) => setNewCustomerName(e.target.value)}
                      placeholder="山田太郎"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      会社名・組織名
                    </label>
                    <input
                      type="text"
                      value={newCustomerCompany}
                      onChange={(e) => setNewCustomerCompany(e.target.value)}
                      placeholder="株式会社サンプル"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                    <Lightbulb className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    <p>メモ保存時に顧客情報が自動的に作成されます</p>
                  </div>
                </div>
              </div>
            )}

            {/* 通話メモ */}
            <section className="flex-1">
              <h4 className="font-medium text-sm text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                <FileEdit className="w-4 h-4" />
                通話メモ
              </h4>
              <InCallNoteEditor
                value={note}
                onChange={setNote}
                tags={tags}
                onTagsChange={setTags}
                callSid={callSidRef.current}
                organizationId={user?.attributes?.['custom:organizationId']}
                phoneNumber={usedPhoneNumber}
                autoSaveStatus={saveStatus}
                autoSaveLastSaved={lastSaved}
                onManualSave={handleManualSave}
                isCallActive={isInCall() && !isCallEnded}
              />
            </section>

            {/* ブロック機能 */}
            {!isAnonymousNumber(callerNumber || lastCallerNumberRef.current) && (
              <section className="border-t border-gray-200 dark:border-gray-700 pt-4">
                {blockStatus?.isBlocked ? (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                    <div className="flex items-start gap-2 mb-2">
                      <ShieldAlert className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h5 className="font-semibold text-sm text-red-900 dark:text-red-100">
                          ブロック済み
                        </h5>
                        <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                          この番号はブロックリストに登録されています
                        </p>
                      </div>
                    </div>
                    {blockStatus.blockInfo && (
                      <div className="text-xs text-red-700 dark:text-red-300 space-y-1 mb-3">
                        <div>
                          <span className="font-medium">理由:</span> {blockStatus.blockInfo.reason}
                        </div>
                        {blockStatus.blockInfo.notes && (
                          <div>
                            <span className="font-medium">メモ:</span> {blockStatus.blockInfo.notes}
                          </div>
                        )}
                        {blockStatus.blockInfo.blockedBy && (
                          <div>
                            <span className="font-medium">登録者:</span> {blockStatus.blockInfo.blockedBy}
                          </div>
                        )}
                      </div>
                    )}
                    <button
                      onClick={handleUnblock}
                      disabled={isBlockLoading}
                      className="w-full px-3 py-1.5 bg-white dark:bg-gray-800 text-red-700 dark:text-red-300 border border-red-300 dark:border-red-700 rounded text-sm hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50 transition"
                    >
                      {isBlockLoading ? 'ブロック解除中...' : 'ブロックを解除'}
                    </button>
                  </div>
                ) : (
                  <div>
                    {!showBlockConfirm ? (
                      <button
                        onClick={() => setShowBlockConfirm(true)}
                        disabled={isBlockLoading}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 disabled:opacity-50 transition"
                      >
                        <Ban className="w-4 h-4" />
                        この番号をブロック
                      </button>
                    ) : (
                      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                        <h5 className="font-semibold text-sm text-red-900 dark:text-red-100 mb-2">
                          ブロック理由を選択
                        </h5>
                        <select
                          value={blockReason}
                          onChange={(e) => setBlockReason(e.target.value)}
                          className="w-full px-3 py-2 border border-red-300 dark:border-red-700 rounded text-sm mb-3 dark:bg-gray-800 dark:text-white"
                        >
                          <option value="迷惑電話">迷惑電話</option>
                          <option value="営業電話">営業電話</option>
                          <option value="詐欺の疑い">詐欺の疑い</option>
                          <option value="クレーマー">クレーマー</option>
                          <option value="その他">その他</option>
                        </select>
                        <div className="flex gap-2">
                          <button
                            onClick={handleBlock}
                            disabled={isBlockLoading}
                            className="flex-1 px-3 py-1.5 bg-red-600 text-white rounded text-sm hover:bg-red-700 disabled:opacity-50 transition"
                          >
                            {isBlockLoading ? 'ブロック中...' : 'ブロック実行'}
                          </button>
                          <button
                            onClick={() => {
                              setShowBlockConfirm(false)
                              setBlockReason('迷惑電話')
                            }}
                            disabled={isBlockLoading}
                            className="px-3 py-1.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-sm hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 transition"
                          >
                            キャンセル
                          </button>
                        </div>
                        {note && (
                          <p className="text-xs text-red-600 dark:text-red-400 mt-2">
                            ※ 通話メモがブロック理由に自動的に含まれます
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </section>
            )}

            {/* 最近の通話履歴（既存顧客のみ） */}
            {customer && (
              <section>
                <h4 className="font-medium text-sm text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                  <ScrollText className="w-4 h-4" />
                  最近の通話履歴
                </h4>
                {callHistoryLoading ? (
                  <div className="flex justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  </div>
                ) : (
                  <CallHistoryTable
                    callHistory={callHistory}
                    displayMode="accordion"
                    onEditMemo={handleEditMemo}
                    emptyMessage="通話履歴がありません"
                  />
                )}
              </section>
            )}
          </div>

          {/* 通話終了後の保存ボタン */}
          {isCallEnded && (
            <div className="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-4 shadow-lg">
              <div className="flex gap-2">
                <button
                  onClick={handleSaveAndClose}
                  disabled={isSaving}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  <Save className="w-4 h-4" />
                  {isSaving ? '保存中...' : note ? 'メモを保存して閉じる' : '保存して閉じる'}
                </button>
                <button
                  onClick={handleCloseWithoutSave}
                  disabled={isSaving}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 transition"
                >
                  閉じる
                </button>
              </div>
              {!note && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                  メモが入力されていません
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* 通話メモ編集モーダル */}
      {showMemoEditor && selectedCall && (
        <CallMemoEditor
          call={selectedCall}
          onClose={() => {
            setShowMemoEditor(false)
            setSelectedCall(null)
          }}
          onSave={handleMemoSave}
        />
      )}
    </aside>
  )
}
